/* ==========================================================================
   FOOTER PAGE — Vanilla ES6
   Accordion (mobile) · Newsletter UX states · Shopify live-preview hooks
   No globals · listeners cleaned up on section unload
   ========================================================================== */
(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';
  const DEBOUNCE_MS = 150;

  /* ---- small utilities ---- */
  function debounce(fn, wait) {
    let t;
    return function () {
      clearTimeout(t);
      const args = arguments;
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /* ======================================================================
     Footer controller — one instance per rendered footer section
     ====================================================================== */
  class FooterPage {
    constructor(root) {
      this.root = root;
      this.mql = window.matchMedia(MOBILE_QUERY);
      this.allowMultiple = root.dataset.accordionMultiple === 'true';
      this.openFirst = root.dataset.accordionOpenFirst === 'true';

      this.toggles = Array.from(root.querySelectorAll('.footer-page__accordion-toggle'));
      this.bound = {
        toggleClick: this.onToggleClick.bind(this),
        resize: debounce(this.onResize.bind(this), DEBOUNCE_MS),
        mqlChange: this.onBreakpointChange.bind(this),
      };

      this.init();
    }

    init() {
      this.toggles.forEach((btn) => btn.addEventListener('click', this.bound.toggleClick));
      window.addEventListener('resize', this.bound.resize);
      if (this.mql.addEventListener) this.mql.addEventListener('change', this.bound.mqlChange);

      this.initNewsletter();
      this.syncAccordions();
    }

    /* ---------------- Accordion ---------------- */
    syncAccordions() {
      const isMobile = this.mql.matches;
      this.toggles.forEach((btn, i) => {
        const body = this.getBody(btn);
        if (!body) return;
        if (!isMobile) {
          // desktop: everything expanded, no inline height
          btn.setAttribute('aria-expanded', 'true');
          body.style.height = '';
        } else {
          const shouldOpen = this.openFirst && i === 0;
          btn.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');
          body.style.height = shouldOpen ? 'auto' : '0px';
        }
      });
    }

    getBody(btn) {
      const id = btn.getAttribute('aria-controls');
      return id ? this.root.querySelector('#' + CSS.escape(id)) : null;
    }

    onToggleClick(e) {
      if (!this.mql.matches) return; // accordion only on mobile
      const btn = e.currentTarget;
      const body = this.getBody(btn);
      if (!body) return;
      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        this.collapse(btn, body);
      } else {
        if (!this.allowMultiple) {
          this.toggles.forEach((other) => {
            if (other !== btn && other.getAttribute('aria-expanded') === 'true') {
              this.collapse(other, this.getBody(other));
            }
          });
        }
        this.expand(btn, body);
      }
    }

    expand(btn, body) {
      btn.setAttribute('aria-expanded', 'true');
      body.style.height = body.scrollHeight + 'px';
      const done = () => {
        body.style.height = 'auto';
        body.removeEventListener('transitionend', done);
      };
      body.addEventListener('transitionend', done);
    }

    collapse(btn, body) {
      btn.setAttribute('aria-expanded', 'false');
      // from auto -> fixed -> 0 so the transition runs
      body.style.height = body.scrollHeight + 'px';
      void body.offsetHeight; // reflow
      body.style.height = '0px';
    }

    onResize() { this.syncAccordions(); }
    onBreakpointChange() { this.syncAccordions(); }

    /* ---------------- Newsletter ---------------- */
    initNewsletter() {
      this.form = this.root.querySelector('.footer-page__news-form');
      if (!this.form) return;
      this.formEl = this.form.querySelector('form');
      this.btn = this.form.querySelector('.footer-page__news-btn');
      this.msg = this.root.querySelector('.footer-page__news-message');
      if (!this.formEl) return;

      this.bound.submit = this.onSubmit.bind(this);
      this.formEl.addEventListener('submit', this.bound.submit);

      // Reflect Shopify-returned post states (success / error) after redirect
      this.reflectServerState();
    }

    reflectServerState() {
      if (!this.msg) return;
      const params = new URLSearchParams(window.location.search);
      // Shopify appends ?customer_posted=true on success
      if (params.get('customer_posted') === 'true') {
        this.setMessage(this.msg.dataset.success || 'Thanks for subscribing!', 'success');
      }
      const serverError = this.root.querySelector('.footer-page__server-error');
      if (serverError && serverError.textContent.trim()) {
        this.setMessage(serverError.textContent.trim(), 'error');
      }
    }

    onSubmit(e) {
      const input = this.formEl.querySelector('input[type="email"]');
      if (!input) return;
      const value = input.value.trim();
      const valid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value);

      if (!valid) {
        e.preventDefault();
        this.setMessage(this.msg ? (this.msg.dataset.error || 'Please enter a valid email.') : '', 'error');
        input.focus();
        return;
      }
      // prevent duplicate submits + show loading; let Shopify handle the POST
      if (this.btn) {
        this.btn.setAttribute('aria-busy', 'true');
        this.btn.disabled = true;
      }
    }

    setMessage(text, type) {
      if (!this.msg) return;
      this.msg.textContent = text;
      this.msg.classList.remove('footer-page__news-message--success', 'footer-page__news-message--error');
      this.msg.classList.add('footer-page__news-message--' + type);
    }

    /* ---------------- Teardown ---------------- */
    destroy() {
      this.toggles.forEach((btn) => btn.removeEventListener('click', this.bound.toggleClick));
      window.removeEventListener('resize', this.bound.resize);
      if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.bound.mqlChange);
      if (this.formEl && this.bound.submit) this.formEl.removeEventListener('submit', this.bound.submit);
    }
  }

  /* ======================================================================
     Registry — supports multiple footers + Shopify Theme Editor lifecycle
     ====================================================================== */
  const instances = new Map();

  function mount(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new FooterPage(root));
  }
  function unmount(root) {
    const inst = instances.get(root);
    if (inst) { inst.destroy(); instances.delete(root); }
  }
  function mountAll() {
    document.querySelectorAll('.footer-page').forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  /* Shopify Theme Editor live preview */
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('.footer-page') ||
      (e.target.classList && e.target.classList.contains('footer-page') ? e.target : null);
    if (root) mount(root);
  });
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('.footer-page');
    if (root) unmount(root);
  });
})();
